import { useCallback, useMemo, useState } from "react";

import { ADMIN_NAV_SECTIONS } from "@/routing/adminNavModel";
import {
  findAdminNavLocation,
  getAdminNavPageLandingPath,
} from "@/routing/adminNavModelQueries";
import type { AdminRouteAccessArgs } from "@/routing/adminRoutes";

/** Nav PRD §5: a new admin starts with Emails pinned. */
export const DEFAULT_PINNED_PAGE_IDS: readonly string[] = ["emails"];
const MAX_PINNED_PAGES = 12;
export const getPinnedPagesStorageKey = (adminId: string) =>
  `hc-admin-pinned-pages:${adminId}`;

export interface PinnedPage {
  id: string;
  label: string;
  sectionLabel: string;
  path: string;
}

const readStoredPinnedIds = (adminId: string): string[] => {
  try {
    const raw = window.localStorage.getItem(getPinnedPagesStorageKey(adminId));
    if (raw === null) return [...DEFAULT_PINNED_PAGE_IDS];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === "string")
      : [...DEFAULT_PINNED_PAGE_IDS];
  } catch {
    return [...DEFAULT_PINNED_PAGE_IDS];
  }
};

const PAGES = ADMIN_NAV_SECTIONS.flatMap((section) =>
  section.pages.map((page) => ({ page, section })),
);

/**
 * F-205: each admin's own pinned pages. A pin is a page of the navigation
 * map, so it only shows while that page is open to the admin, and it opens
 * the same access-checked landing address as the sidebar.
 */
export function usePinnedPages(args: {
  adminId: string | null | undefined;
  pathname: string;
  routeAccess: AdminRouteAccessArgs;
  hiddenPaths?: ReadonlySet<string>;
}) {
  const { hiddenPaths, pathname, routeAccess } = args;
  const key = args.adminId ?? "anonymous";
  const [overrides, setOverrides] = useState<Record<string, string[]>>({});
  const pinnedIds = useMemo(
    () => overrides[key] ?? readStoredPinnedIds(key),
    [key, overrides],
  );

  const resolvePage = useCallback(
    (pageId: string): PinnedPage | null => {
      const entry = PAGES.find(({ page }) => page.id === pageId);
      if (!entry) return null;
      const path = getAdminNavPageLandingPath(entry.page, routeAccess);
      if (!path || hiddenPaths?.has(path)) return null;
      return {
        id: entry.page.id,
        label: entry.page.label,
        sectionLabel: entry.section.label,
        path,
      };
    },
    [hiddenPaths, routeAccess],
  );

  const pinned = useMemo(
    () =>
      pinnedIds.flatMap((pageId) => {
        const page = resolvePage(pageId);
        return page ? [page] : [];
      }),
    [pinnedIds, resolvePage],
  );

  const currentPageId = findAdminNavLocation(pathname)?.page?.id ?? null;
  const currentPage = currentPageId ? resolvePage(currentPageId) : null;

  const save = useCallback(
    (next: string[]) => {
      try {
        window.localStorage.setItem(
          getPinnedPagesStorageKey(key),
          JSON.stringify(next),
        );
      } catch {
        // Pins still work for this visit.
      }
      setOverrides((current) => ({ ...current, [key]: next }));
    },
    [key],
  );

  const unpin = useCallback(
    (pageId: string) => save(pinnedIds.filter((id) => id !== pageId)),
    [pinnedIds, save],
  );

  const toggleCurrent = useCallback(() => {
    if (!currentPage) return;
    save(
      pinnedIds.includes(currentPage.id)
        ? pinnedIds.filter((id) => id !== currentPage.id)
        : [...pinnedIds, currentPage.id].slice(-MAX_PINNED_PAGES),
    );
  }, [currentPage, pinnedIds, save]);

  return {
    pinned,
    currentPage,
    isCurrentPinned: Boolean(currentPage && pinnedIds.includes(currentPage.id)),
    toggleCurrent,
    unpin,
  };
}
